/**
 * 閲覧履歴管理モジュール
 *
 * 商品ページの閲覧履歴をブラウザのローカルストレージに保存し、
 * 最近閲覧した商品を表示する機能を提供します。
 */
class BrowsingHistory {
  constructor() {
    this.storageKey = 'shopify_browsing_history';
    this.maxItems = 20; // 保存する最大商品数
  }

  /**
   * 商品を閲覧履歴に追加
   * @param {Object} product - 商品情報
   * @param {string|number} product.id - 商品ID
   * @param {string} product.handle - 商品ハンドル
   */
  addProduct(product) {
    if (!product || !product.id || !product.handle) {
      console.warn('BrowsingHistory: Invalid product data (id and handle required)', product);
      return;
    }

    const history = this.getHistory();

    // 既存の同じ商品を削除（重複を避けるため）
    const filteredHistory = history.filter((item) => item.id !== product.id);

    // 新しい商品を先頭に追加（最小限のデータのみ）
    const newProduct = {
      id: product.id,
      handle: product.handle,
      viewedAt: new Date().toISOString(),
    };

    filteredHistory.unshift(newProduct);

    // 最大数を超えた分を削除
    const trimmedHistory = filteredHistory.slice(0, this.maxItems);

    // ローカルストレージに保存
    this.saveHistory(trimmedHistory);

    // カスタムイベントを発火（履歴が更新されたことを通知）
    this.dispatchUpdateEvent();
  }

  /**
   * 閲覧履歴を取得
   * @returns {Array} 閲覧履歴の配列
   */
  getHistory() {
    try {
      const data = localStorage.getItem(this.storageKey);
      const history = data ? JSON.parse(data) : [];

      return history;
    } catch (error) {
      console.error('BrowsingHistory: Failed to get history', error);
      return [];
    }
  }

  /**
   * 閲覧履歴を保存
   * @param {Array} history - 保存する履歴配列
   */
  saveHistory(history) {
    try {
      const jsonData = JSON.stringify(history);
      localStorage.setItem(this.storageKey, jsonData);
    } catch (error) {
      console.error('BrowsingHistory: Failed to save history', error);
    }
  }

  /**
   * 指定した商品を履歴から削除
   * @param {string|number} productId - 削除する商品のID
   */
  removeProduct(productId) {
    const history = this.getHistory();
    const filteredHistory = history.filter((item) => item.id !== productId);
    this.saveHistory(filteredHistory);
    this.dispatchUpdateEvent();
  }

  /**
   * 閲覧履歴をクリア
   */
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
      this.dispatchUpdateEvent();
    } catch (error) {
      console.error('BrowsingHistory: Failed to clear history', error);
    }
  }

  /**
   * 特定の商品IDを除外した履歴を取得（現在の商品ページで使用）
   * @param {string|number} excludeProductId - 除外する商品ID
   * @param {number} limit - 取得する最大数
   * @returns {Array} フィルタリングされた閲覧履歴
   */
  getHistoryExcluding(excludeProductId, limit = 10) {
    const history = this.getHistory();
    const filtered = excludeProductId ? history.filter((item) => item.id != excludeProductId) : history;
    const result = limit ? filtered.slice(0, limit) : filtered;

    return result;
  }

  /**
   * 履歴更新イベントを発火
   */
  dispatchUpdateEvent() {
    const event = new CustomEvent('browsinghistory:updated', {
      detail: { history: this.getHistory() },
    });
    document.dispatchEvent(event);
  }
}

// グローバルインスタンスを作成
if (window.browsingHistory) {
  console.warn('BrowsingHistory: Global instance already exists, skipping creation');
} else {
  window.browsingHistory = new BrowsingHistory();
}

/**
 * 商品ページで閲覧履歴を自動的に記録するカスタム要素
 *
 * 使用例:
 * <browsing-history-tracker
 *   data-product-id="12345"
 *   data-product-title="商品名"
 *   data-product-url="/products/product-handle"
 *   data-product-handle="product-handle"
 *   data-product-image="https://..."
 *   data-product-price="1000"
 *   data-product-compare-at-price="1500"
 *   data-product-available="true">
 * </browsing-history-tracker>
 */
class BrowsingHistoryTracker extends HTMLElement {
  connectedCallback() {
    // ページ読み込み時に商品を履歴に追加
    this.trackProduct();
  }

  trackProduct() {
    const productData = {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
    };

    if (window.browsingHistory) {
      window.browsingHistory.addProduct(productData);
    } else {
      console.error('BrowsingHistory: window.browsingHistory not available');
    }
  }
}

customElements.define('browsing-history-tracker', BrowsingHistoryTracker);
